import * as React from 'react';

import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  black,
  gray,
  grayPlaceholder,
  primarycolor
} from '../../constants/Colors';
import {
  formExtraNarrowMb,
  formLabelWrapper,
  formNarrowMb,
  formWideMb
} from '../../constants/Location';

import type { SxProps } from '@mui/material';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type selectProps = React.ComponentProps<typeof Select>;

export interface List {
  value: number | string;
  option: string;
  disabled?: boolean;
}

type Props = selectProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  width?: string;
  list: List[];
  formMb?: string | undefined;
  blank?: boolean;
  blankText?: string;
  disabled?: boolean;
};

const CustomSelectField = React.forwardRef(
  (props: Props): React.ReactElement => {
    const { t } = useTranslation();

    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      list,
      formMb,
      width,
      blank,
      blankText,
      disabled,
      ...rest
    } = props;

    function inputBorderColor(): SxProps | undefined {
      if (error) {
        return {
          '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: primarycolor
          },
          '@media screen and (max-width:480px)': {
            width: width ?? '100%'
          },
          height: '40px',
          width: width ?? '400px'
        };
      }
      return {
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: black
          },
        '@media screen and (max-width:480px)': {
          width: width ?? '100%'
        },
        height: '40px',
        width: width ?? '400px'
      };
    }

    let inputWidth;
    let inputSpWidth;
    if (width === 'narrow') {
      inputWidth = '180px';
      inputSpWidth = '148px';
    } else if (width === 'search') {
      inputWidth = '100%';
      inputSpWidth = '100%';
    } else if (width === 'article') {
      inputWidth = '400px';
      inputSpWidth = '100%';
    } else if (width === 'contactDrawer') {
      inputWidth = '268px';
      inputSpWidth = '100%';
    } else {
      inputWidth = '400px';
      inputSpWidth = '311px';
    }

    const styles = {
      formLabelWrapper,
      label: {
        fontSize: '12px'
      },
      required: {
        color: primarycolor,
        fontSize: '12px',
        marginLeft: '10px'
      },
      select: {
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: black
          },
        '@media screen and (max-width:480px)': {
          width: inputSpWidth
        },
        height: '40px',
        inputBorderColor,
        width: inputWidth
      }
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    const inputformMb = (marginBottom: string | undefined): SxProps => {
      if (marginBottom === 'zero') return formExtraNarrowMb;
      if (marginBottom === 'narrow') return formNarrowMb;
      return formWideMb;
    };

    return (
      <Typography sx={inputformMb(formMb)} align="left" component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray} sx={styles.label} component="span">
            {labelText}
          </Typography>
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <Typography sx={{ textAlign: 'left' }} component="div">
          <FormControl sx={{ width: '100%' }} {...(error && { error: true })}>
            <Select
              {...rest}
              sx={styles.select}
              {...(disabled && { disabled })}
            >
              {blank && (
                <MenuItem
                  value={0}
                  sx={{ color: black }}
                  {...(isRequired && { disabled: true })}
                >
                  <Typography
                    component="p"
                    sx={{
                      color: grayPlaceholder,
                      textAlign: 'left',
                      whiteSpace: 'break-spaces'
                    }}
                  >
                    {t('industry.empty')}
                  </Typography>
                </MenuItem>
              )}
              {!blank && (
                <MenuItem
                  value={0}
                  sx={{ color: black }}
                  {...(isRequired && { disabled: true })}
                >
                  <Typography
                    component="p"
                    sx={{ color: grayPlaceholder, textAlign: 'left' }}
                  >
                    {blankText}
                  </Typography>
                </MenuItem>
              )}
              {list.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{ color: black, whiteSpace: 'normal' }}
                  {...(item.disabled && { disabled: item.disabled })}
                >
                  <Typography
                    component="p"
                    sx={{
                      color: black,
                      textAlign: 'left',
                      whiteSpace: 'break-spaces'
                    }}
                  >
                    {item.option}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Typography>
        <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
      </Typography>
    );
  }
);
CustomSelectField.displayName = 'CustomSelectField';
export default CustomSelectField;
